import { useState, useEffect } from "react";
import { supabase } from "../supabase";
import { toast } from "../Toast";
import { API_URL, DEV_MODE } from "../config/env";
import { DISPLAY_NAME_KEY } from "../config/constants";
import RedeemCodeBlock from "./RedeemCodeBlock";
import { track } from "../lib/analytics";
import DeleteAccountBlock from "./DeleteAccountBlock";

export default function AccountPage({ subscription, userId, userEmail, onLogout, usageCount = 0 }) {
  const [displayName, setDisplayName] = useState(() => localStorage.getItem(DISPLAY_NAME_KEY) || "");
  const [nameSaved, setNameSaved] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(null);
  const [stripeDetails, setStripeDetails] = useState(null);

  // Fetch enriched subscription details from backend (Stripe data)
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        const token = sess?.session?.access_token;
        if (!token) return; // demo / no session: skip the call
        const r = await fetch(`${API_URL}/check-subscription`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await r.json();
        if (data.active) setStripeDetails(data);
      } catch {}
    })();
  }, [userId]);

  const cardBrand = stripeDetails?.card_brand || subscription?.card_brand;
  const cardLast4 = stripeDetails?.card_last4 || subscription?.card_last4;

  const effectiveStatus = stripeDetails?.stripe_status || subscription?.stripe_status;
  const isTrialing = effectiveStatus === "trialing";
  const isActive = effectiveStatus === "active";
  const isPro = subscription?.plan === "pro" || DEV_MODE;
  const isLite = subscription?.plan === "lite";
  const isLiteActive = isLite && isActive;
  const isStarterActive = subscription?.plan === "starter" && isActive;
  const isTrial = isTrialing;

  const trialPlanLabel =
    subscription?.plan === "pro" ? "Pitcherly Pro"
    : subscription?.plan === "lite" ? "Pitcherly Lite"
    : "Pitcherly Starter";
  const planDisplay =
    isPro && isActive ? "Pitcherly Pro · $99/mo"
    : isLiteActive ? "Pitcherly Lite · $19/mo"
    : isStarterActive ? "Pitcherly Starter · $49/mo"
    : isTrialing ? `${trialPlanLabel} · Free Trial`
    : "Pitcherly Starter · Free Trial";
  const statusDisplay = isTrialing ? "Trialing" : "Active";
  const statusColor = isTrialing ? "#f59e0b" : "#22c55e";

  const limit = isTrialing ? 50 : isPro ? Infinity : isLite ? 50 : 200;
  const used = usageCount || 0;
  const usagePct = limit === Infinity ? 0 : Math.min((used / limit) * 100, 100);
  const ringR = 27;
  const ringC = 2 * Math.PI * ringR;
  const ringOffset = ringC - (usagePct / 100) * ringC;
  const usageLabel = isTrialing
    ? `${used} of 50 used in trial`
    : limit === Infinity
    ? "Unlimited generations"
    : `${used} of ${limit} used this month`;

  const initials = (displayName || userEmail || "?")
    .split(/[\s@]+/)
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase() || "")
    .join("");

  const saveName = () => {
    localStorage.setItem(DISPLAY_NAME_KEY, displayName);
    setNameSaved(true);
    setTimeout(() => setNameSaved(false), 1500);
  };

  const handlePasswordUpdate = async () => {
    if (newPassword.length < 6) { setPwMsg({ type: "error", text: "Password must be at least 6 characters" }); return; }
    if (newPassword !== confirmPassword) { setPwMsg({ type: "error", text: "Passwords do not match" }); return; }
    setPwLoading(true); setPwMsg(null);
    if (supabase) {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) setPwMsg({ type: "error", text: error.message });
      else { setPwMsg({ type: "success", text: "Password updated" }); setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); }
    }
    setPwLoading(false);
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      const res = await fetch(`${API_URL}/cancel-subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await res.json();
      if (data.success) window.location.reload();
      else toast.error(data.detail || "Failed to cancel");
    } catch { toast.error("Could not connect to server"); }
    setCancelling(false); setCancelConfirm(false);
  };

  const handleUpgrade = async (plan = "pro", skipTrial = false) => {
    setUpgrading(true);
    track("upgrade_clicked", { target_plan: plan, skip_trial: skipTrial, source: "account" });
    try {
      const res = await fetch(`${API_URL}/create-checkout-session`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, user_id: userId, user_email: userEmail, skip_trial: skipTrial }),
      });
      const data = await res.json();
      if (data.url) { track("checkout_started", { target_plan: plan, source: "account" }); window.location.href = data.url; }
      else toast.error(data.detail || "Failed to create checkout");
    } catch { toast.error("Could not connect to server"); }
    setUpgrading(false);
  };

  const handleBillingPortal = async () => {
    setPortalLoading(true); setPortalError(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      const res = await fetch(`${API_URL}/create-billing-portal-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else if (res.status === 404 || res.status === 400) setPortalError("No billing account found. Complete a subscription first to manage billing.");
      else setPortalError("Unable to open billing portal. Please contact support.");
    } catch { setPortalError("Unable to open billing portal. Please contact support."); }
    setPortalLoading(false);
  };

  const rawNextBilling = stripeDetails?.next_billing || subscription?.next_billing;
  const rawTrialEnd = stripeDetails?.trial_end;
  const formatDate = (ts) => ts ? new Date(ts * 1000).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : null;
  const estimatedTrialEnd = subscription?.created_at
    ? (() => { const d = new Date(subscription.created_at); d.setDate(d.getDate() + 7); return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); })()
    : null;
  const billingLabel = isTrial ? "Trial ends" : "Next billing";
  const billingDate = isTrial
    ? (formatDate(rawTrialEnd) || formatDate(rawNextBilling) || estimatedTrialEnd || "—")
    : (formatDate(rawNextBilling) || "—");

  return (
    <div className="layout">
      {/* LEFT — PROFILE */}
      <div className="input-panel" style={{alignItems:'stretch'}}>
        <div className="acct-profile-card">
          <div className="acct-avatar">{initials}</div>
          <input
            className="acct-name-input"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            placeholder="Your name"
          />
          <div className="acct-email">{userEmail}</div>
          <span className={`acct-plan-pill ${isPro ? "pro" : "starter"}`}>
            {isPro
              ? (isTrial ? "PRO TRIAL" : "PRO")
              : isLite
              ? (isTrial ? "LITE TRIAL" : "LITE")
              : (isTrial ? "STARTER TRIAL" : "STARTER")}
          </span>
          <button className={`acct-save-btn ${nameSaved ? "saved" : ""}`} onClick={saveName}>
            {nameSaved ? "✓ Saved" : "Save Name"}
          </button>
        </div>
      </div>

      {/* RIGHT — CARDS */}
      <div className="output-panel" style={{flex:1}}>
        <div className="acct-cards">

          {/* SECURITY */}
          <div className="section">
            <div className="section-head">Security</div>
            <div className="section-body">
              <div className="field">
                <label>Current Password</label>
                <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Enter current password" />
              </div>
              <div className="field">
                <label>New Password</label>
                <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters" />
              </div>
              <div className="field">
                <label>Confirm Password</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password" />
              </div>
              <button className="acct-btn accent" onClick={handlePasswordUpdate} disabled={pwLoading || !newPassword} style={{marginTop:4}}>
                {pwLoading ? "Updating..." : "Update Password"}
              </button>
              {pwMsg && <div className={`acct-msg ${pwMsg.type}`} style={{marginTop:4}}>{pwMsg.text}</div>}
            </div>
          </div>

          {/* SUBSCRIPTION */}
          <div className="section">
            <div className="section-head">Subscription</div>
            <div className="section-body">
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
                <div>
                  <div style={{fontSize:14,fontWeight:600,color:'var(--text)'}}>
                    {planDisplay}
                  </div>
                  <div style={{fontSize:12,color:'var(--text2)',marginTop:3,display:'flex',alignItems:'center',gap:6}}>
                    <span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:statusColor}} /> {statusDisplay}
                  </div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontSize:11,color:'var(--text3)'}}>{billingLabel}</div>
                  <div style={{fontSize:13,fontWeight:500,color:'var(--text)',marginTop:1}}>{billingDate}</div>
                </div>
              </div>
              {isPro && isActive ? (
                <div style={{fontSize:13,color:'var(--green)',fontWeight:500,textAlign:'center',padding:'8px 0'}}>You're on our best plan &#10003;</div>
              ) : isLiteActive ? (
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  {/* Starter card */}
                  <div style={{padding:14,background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:10}}>
                    <div style={{fontSize:14,fontWeight:600,color:'var(--text)',marginBottom:8}}>Starter — $49/mo</div>
                    <div style={{fontSize:12,color:'var(--text2)',lineHeight:1.5,marginBottom:10}}>
                      ✦ 200 AI generations per month<br/>
                      ✦ 10 verified property lookups/month<br/>✦ Market-data CMAs (live estimate + comps)<br/>
                      ✦ All 10 agent writing tools
                    </div>
                    <button className="acct-btn" onClick={() => handleUpgrade("starter")} disabled={upgrading} style={{width:'100%'}}>
                      {upgrading ? "Redirecting..." : "Upgrade to Starter"}
                    </button>
                  </div>
                  {/* Pro card */}
                  <div style={{padding:14,background:'rgba(212,145,92,0.06)',border:'1px solid var(--accent)',borderRadius:10}}>
                    <div style={{fontSize:14,fontWeight:600,color:'var(--text)',marginBottom:8}}>Pro — $99/mo</div>
                    <div style={{fontSize:12,color:'var(--text2)',lineHeight:1.5,marginBottom:10}}>
                      ✦ <span style={{color:'var(--accent)',fontWeight:600}}>Unlimited</span> AI generations<br/>
                      ✦ 50 verified property lookups/month<br/>✦ Market-data CMAs (live estimate + comps)<br/>
                      ✦ Priority generation speed
                    </div>
                    <button className="acct-btn upgrade" onClick={() => handleUpgrade("pro")} disabled={upgrading} style={{width:'100%'}}>
                      {upgrading ? "Redirecting..." : "Upgrade to Pro"}
                    </button>
                  </div>
                </div>
              ) : isTrialing ? (
                <>
                  <div style={{display:'flex',flexDirection:'column',gap:10}}>
                    {/* Lite card */}
                    <div style={{padding:14,background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:10}}>
                      <div style={{fontSize:14,fontWeight:600,color:'var(--text)',marginBottom:8}}>Lite — $19/mo</div>
                      <div style={{fontSize:12,color:'var(--text2)',lineHeight:1.5,marginBottom:10}}>
                        ✦ 50 AI generations per month<br/>
                        ✦ All 10 agent writing tools<br/>
                        ✦ No property lookups<br/>
                        ✦ Copy & send directly from Pitcherly
                      </div>
                      <button className="acct-btn" onClick={() => handleUpgrade("lite")} disabled={upgrading} style={{width:'100%'}}>
                        {upgrading ? "Redirecting..." : "Switch to Lite"}
                      </button>
                    </div>
                    {/* Starter card */}
                    <div style={{padding:14,background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:10}}>
                      <div style={{fontSize:14,fontWeight:600,color:'var(--text)',marginBottom:8}}>Starter — $49/mo</div>
                      <div style={{fontSize:12,color:'var(--text2)',lineHeight:1.5,marginBottom:10}}>
                        ✦ 200 AI generations per month<br/>
                        ✦ 10 verified property lookups/month<br/>✦ Market-data CMAs (live estimate + comps)<br/>
                        ✦ All 10 agent writing tools<br/>
                        ✦ MLS listings, follow-ups, bios & more<br/>
                        ✦ Copy & send directly from Pitcherly
                      </div>
                      <button className="acct-btn" onClick={() => handleUpgrade("starter")} disabled={upgrading} style={{width:'100%'}}>
                        {upgrading ? "Redirecting..." : "Upgrade to Starter"}
                      </button>
                    </div>
                    {/* Pro card */}
                    <div style={{padding:14,background:'rgba(212,145,92,0.06)',border:'1px solid var(--accent)',borderRadius:10}}>
                      <div style={{fontSize:14,fontWeight:600,color:'var(--text)',marginBottom:8}}>Pro — $99/mo</div>
                      <div style={{fontSize:12,color:'var(--text2)',lineHeight:1.5,marginBottom:10}}>
                        ✦ <span style={{color:'var(--accent)',fontWeight:600}}>Unlimited</span> AI generations<br/>
                        ✦ 50 verified property lookups/month<br/>✦ Market-data CMAs (live estimate + comps)<br/>
                        ✦ Everything in Starter<br/>
                        ✦ Priority generation speed<br/>
                        ✦ Best for high-volume agents & teams
                      </div>
                      <button className="acct-btn upgrade" onClick={() => handleUpgrade("pro")} disabled={upgrading} style={{width:'100%'}}>
                        {upgrading ? "Redirecting..." : "Upgrade to Pro"}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                /* Active Starter — show Lite (downgrade) and Pro (upgrade) cards */
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  {/* Lite card */}
                  <div style={{padding:14,background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:10}}>
                    <div style={{fontSize:14,fontWeight:600,color:'var(--text)',marginBottom:8}}>Lite — $19/mo</div>
                    <div style={{fontSize:12,color:'var(--text2)',lineHeight:1.5,marginBottom:10}}>
                      ✦ 50 AI generations per month<br/>
                      ✦ All 10 agent writing tools<br/>
                      ✦ No property lookups<br/>
                      ✦ Copy & send directly from Pitcherly
                    </div>
                    <button className="acct-btn" onClick={() => handleUpgrade("lite")} disabled={upgrading} style={{width:'100%'}}>
                      {upgrading ? "Redirecting..." : "Switch to Lite"}
                    </button>
                  </div>
                  {/* Pro card */}
                  <div style={{padding:14,background:'rgba(212,145,92,0.06)',border:'1px solid var(--accent)',borderRadius:10}}>
                    <div style={{fontSize:14,fontWeight:600,color:'var(--text)',marginBottom:8}}>Pro — $99/mo</div>
                    <div style={{fontSize:12,color:'var(--text2)',lineHeight:1.5,marginBottom:10}}>
                      ✦ <span style={{color:'var(--accent)',fontWeight:600}}>Unlimited</span> AI generations<br/>
                      ✦ 50 verified property lookups/month<br/>✦ Market-data CMAs (live estimate + comps)<br/>
                      ✦ Everything in Starter<br/>
                      ✦ Priority generation speed<br/>
                      ✦ Best for high-volume agents & teams
                    </div>
                    <button className="acct-btn upgrade" onClick={() => handleUpgrade("pro")} disabled={upgrading} style={{width:'100%'}}>
                      {upgrading ? "Redirecting..." : "Upgrade to Pro"}
                    </button>
                  </div>
                </div>
              )}
              {!cancelConfirm ? (
                <button className="cancel-link" onClick={() => setCancelConfirm(true)} style={{marginTop: isPro ? 0 : 8}}>
                  Cancel subscription
                </button>
              ) : (
                <div className="cancel-confirm" style={{marginTop:8}}>
                  Cancel your subscription? You'll lose access at the end of your billing period.
                  <div className="cancel-confirm-actions">
                    <button className="cancel-confirm-btn yes" onClick={handleCancel} disabled={cancelling}>
                      {cancelling ? "Cancelling..." : "Yes, cancel"}
                    </button>
                    <button className="cancel-confirm-btn no" onClick={() => setCancelConfirm(false)}>
                      Keep subscription
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* PAYMENT METHOD */}
          <div className="section">
            <div className="section-head">Payment Method</div>
            <div className="section-body">
              {cardBrand && cardLast4 ? (
                <div style={{fontSize:13,color:'var(--text)',marginBottom:12,display:'flex',alignItems:'center',gap:8}}>
                  <span style={{fontSize:18}}>💳</span>
                  <span style={{textTransform:'capitalize'}}>{cardBrand}</span> ending in <strong>{cardLast4}</strong>
                </div>
              ) : (
                <div style={{fontSize:13,color:'var(--text3)',marginBottom:12}}>No payment method on file</div>
              )}
              <button className="acct-btn" onClick={handleBillingPortal} disabled={portalLoading}>
                {portalLoading ? "Redirecting..." : "Update Payment Method"}
              </button>
              <div style={{fontSize:11,color:'var(--text3)',marginTop:6}}>You'll be redirected to Stripe's secure billing portal</div>
              {portalError && <div className="acct-msg error" style={{marginTop:8}}>{portalError}</div>}
            </div>
          </div>

          {/* USAGE — Starter only */}
          {!isPro && (
            <div className="section">
              <div className="section-head">Usage</div>
              <div className="section-body">
                <div className="usage-ring-wrap">
                  <div className="usage-ring">
                    <svg width="64" height="64" viewBox="0 0 64 64">
                      <circle className="usage-ring-bg" cx="32" cy="32" r={ringR} />
                      <circle
                        className={`usage-ring-fill ${usagePct > 80 ? "low" : "ok"}`}
                        cx="32" cy="32" r={ringR}
                        strokeDasharray={ringC}
                        strokeDashoffset={ringOffset}
                      />
                    </svg>
                    <div className="usage-ring-text">{used}</div>
                  </div>
                  <div className="usage-ring-info">
                    {usageLabel}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ACCESS CODE */}
          <div className="section">
            <div className="section-head">Access Code</div>
            <div className="section-body">
              <RedeemCodeBlock userId={userId} userEmail={userEmail} compact={true} />
            </div>
          </div>

          {/* DANGER ZONE */}
          <div className="danger-card">
            <div className="section-head">Danger Zone</div>
            <div className="section-body">
              <button className="acct-btn logout" onClick={onLogout}>Log Out</button>
              <DeleteAccountBlock userId={userId} userEmail={userEmail} onDeleted={onLogout} />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
